/* eslint-disable @typescript-eslint/no-unsafe-call */
import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Button,
  Card,
  CardContent,
} from '@mui/material';

interface OAuthMessage {
  type: 'OAUTH_SUCCESS' | 'OAUTH_ERROR';
  code?: string;
  state?: string;
  error?: string;
  errorDescription?: string;
}

const OAuthCallback: React.FC = () => {
  const [isSimulation, setIsSimulation] = useState(false);
  const [platform, setPlatform] = useState<string>('');
  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    // URL에서 인증 코드 추출
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    const state = urlParams.get('state');
    const error = urlParams.get('error');
    const errorDescription = urlParams.get('error_description');

    // 시뮬레이션 모드 확인 (개발 환경)
    if (code && code.startsWith('test_auth_code_')) {
      setIsSimulation(true);
      setPlatform(state || 'unknown');
      return;
    }

    // 부모 창에 메시지 전송하는 안전한 함수
    const sendMessageToParent = (message: OAuthMessage) => {
      if (window.opener && typeof window.opener.postMessage === 'function') {
        window.opener.postMessage(message, window.location.origin);
      }
    };

    // 창 닫기 함수
    const closeWindow = () => {
      try {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call
        (window as any).close();
      } catch (error) {
        console.warn('창을 닫을 수 없습니다:', error);
      }
    };

    if (error) {
      // 에러 발생 시 부모 창에 에러 메시지 전송
      sendMessageToParent({
        type: 'OAUTH_ERROR',
        error,
        errorDescription: errorDescription || undefined,
      });
      closeWindow();
      return;
    }

    if (code) {
      // 성공 시 부모 창에 인증 코드 전송
      sendMessageToParent({
        type: 'OAUTH_SUCCESS',
        code,
        state: state || undefined,
      });
      closeWindow();
    } else {
      // 코드가 없는 경우 에러 처리
      sendMessageToParent({
        type: 'OAUTH_ERROR',
        error: 'no_code',
        errorDescription: '인증 코드를 받지 못했습니다.',
      });
      closeWindow();
    }
  }, []);

  // 시뮬레이션 인증 처리
  const handleSimulationAuth = (success: boolean) => {
    setIsProcessing(true);

    const sendMessageToParent = (message: OAuthMessage) => {
      if (window.opener && typeof window.opener.postMessage === 'function') {
        window.opener.postMessage(message, window.location.origin);
      }
    };

    const closeWindow = () => {
      try {
        (window as any).close();
      } catch (error) {
        console.warn('창을 닫을 수 없습니다:', error);
      }
    };

    setTimeout(() => {
      if (success) {
        sendMessageToParent({
          type: 'OAUTH_SUCCESS',
          code: `test_auth_code_${platform}`,
          state: platform,
        });
      } else {
        sendMessageToParent({
          type: 'OAUTH_ERROR',
          error: 'user_denied',
          errorDescription: '사용자가 인증을 거부했습니다.',
        });
      }
      closeWindow();
    }, 1000);
  };

  // 시뮬레이션 인증 페이지
  if (isSimulation) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh',
          bgcolor: '#f5f5f5',
          p: 3,
        }}
      >
        <Card sx={{ maxWidth: 400, width: '100%', textAlign: 'center' }}>
          <CardContent sx={{ p: 4 }}>
            {isProcessing ? (
              <>
                <CircularProgress size={60} thickness={4} sx={{ mb: 3 }} />
                <Typography
                  variant='h5'
                  fontWeight='600'
                  color='primary.main'
                  gutterBottom
                >
                  인증 처리 중...
                </Typography>
                <Typography variant='body1' color='text.secondary'>
                  잠시만 기다려주세요.
                </Typography>
              </>
            ) : (
              <>
                <Typography
                  variant='h4'
                  fontWeight='bold'
                  color='primary.main'
                  gutterBottom
                >
                  🔐 {platform.toUpperCase()} 인증
                </Typography>
                <Typography variant='h6' color='text.secondary' sx={{ mb: 3 }}>
                  개발 환경 시뮬레이션
                </Typography>
                <Typography
                  variant='body1'
                  color='text.secondary'
                  sx={{ mb: 4 }}
                >
                  Zero Salon 앱이 {platform} 계정에 접근하여 포스팅할 수 있도록
                  허용하시겠습니까?
                </Typography>

                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
                  <Button
                    variant='contained'
                    color='primary'
                    size='large'
                    onClick={() => handleSimulationAuth(true)}
                    sx={{ minWidth: 120 }}
                  >
                    허용
                  </Button>
                  <Button
                    variant='outlined'
                    color='secondary'
                    size='large'
                    onClick={() => handleSimulationAuth(false)}
                    sx={{ minWidth: 120 }}
                  >
                    거부
                  </Button>
                </Box>
              </>
            )}
          </CardContent>
        </Card>
      </Box>
    );
  }

  // 실제 OAuth 처리 중 로딩 페이지
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        bgcolor: '#f5f5f5',
        textAlign: 'center',
        p: 3,
      }}
    >
      <CircularProgress size={60} thickness={4} sx={{ mb: 3 }} />
      <Typography
        variant='h5'
        fontWeight='600'
        color='primary.main'
        gutterBottom
      >
        인증 처리 중...
      </Typography>
      <Typography variant='body1' color='text.secondary'>
        잠시만 기다려주세요. 소셜 미디어 계정 연결을 처리하고 있습니다.
      </Typography>
    </Box>
  );
};

export default OAuthCallback;
